import React, { useState } from 'react';
import { useRouter } from 'next/router';
import ImageSuccess from '@/assets/img/default-success.svg';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { ROUTER_PAGES } from '@/utils/constants';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ModalDefault from '../../Modals/ModalDefault';
import { CreateInstitutionalPageSteps, View } from './View';

interface ICreateInstitutionalPageProps {
  isOpen: boolean;
  handleClose: () => void;
}

const CreateInstitutionalPage = ({
  isOpen,
  handleClose,
}: ICreateInstitutionalPageProps) => {
  const [currentStep, setCurrentStep] = useState<CreateInstitutionalPageSteps>(
    CreateInstitutionalPageSteps.CONFIGURE_COMPANY_NAME
  );
  const [isOpenModalSucces, setIsOpenModalSuccess] = useState(false);
  const { setIsLoading } = useLoading();

  const router = useRouter();
  const { defaultMembership } = useUser();

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const isInstitutionalPageConfigOpen =
    router.query.is_institutional_page_config_open === 'true';

  const backStep = () => {
    if (currentStep !== CreateInstitutionalPageSteps.CONFIGURE_COMPANY_NAME) {
      return setCurrentStep(prevState => prevState - 1);
    }
    handleClose();
  };

  const nextStep = () => {
    if (currentStep !== CreateInstitutionalPageSteps.CONFIGURE_COMPANY_SLUG)
      setCurrentStep(prevState => prevState + 1);
  };

  const handleCloseModalSuccess = () => {
    setIsLoading(true);
    setIsOpenModalSuccess(false);
    handleClose();
    setCurrentStep(CreateInstitutionalPageSteps.CONFIGURE_COMPANY_NAME);
    router
      .push(`${ROUTER_PAGES.site}/${defaultMembership?.company.slug}/editar`)
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Modal
        aria-labelledby="modal-my-account"
        aria-describedby="modal-my-account-description"
        open={isOpen || isInstitutionalPageConfigOpen}
        onClose={handleClose}
      >
        <Box
          sx={{ backgroundColor: '#FFF', overflowY: 'scroll', height: '100vh' }}
        >
          <Box
            sx={{ boxShadow: mdUp ? '0px 1px 1px rgba(0, 0, 0, 0.1)' : null }}
          >
            <Box padding="8px" sx={{ maxWidth: '900px', margin: '0 auto' }}>
              <IconButton
                onClick={
                  isInstitutionalPageConfigOpen
                    ? () => router.push(ROUTER_PAGES.home)
                    : backStep
                }
              >
                <ArrowBackIcon sx={{ color: '#000' }} />
              </IconButton>
            </Box>
          </Box>

          <MainContainer
            sx={{
              padding: '0 16px 38px 16px',
              minHeight: 'calc(100vh - 56px)',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              '& .submitButton': {
                width: 'min(100%, 382px)',
                marginX: 'auto',
                marginTop: mdUp ? '38px' : 'auto',
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gap: '16px',
                textAlign: 'center',
                margin: '-40px 0 32px 0',
                padding: '0 12px',
              }}
            >
              <Typography variant="h6" component="h1">
                Minha página
              </Typography>
              <LinearProgress
                sx={{
                  height: '6px',
                  borderRadius: '10px',
                  backgroundColor: '#D9D9D9',
                  marginTop: mdUp ? 2 : '',
                }}
                value={33 * currentStep}
                variant="determinate"
                data-testid="bar-progress"
              />
            </Box>

            <View
              currentStep={currentStep}
              nextStep={nextStep}
              onSuccess={() => setIsOpenModalSuccess(true)}
            />
          </MainContainer>
        </Box>
      </Modal>
      <ModalDefault
        isOpen={isOpenModalSucces}
        onClose={handleCloseModalSuccess}
        src={ImageSuccess}
        alt="Imagem representando sucesso de criação da página institucional"
        title="Perfeito!"
        subtitle="Agora vamos personalizar mais a página da sua empresa, que já está disponível no ecossistema do Obra Play!"
      />
    </>
  );
};

export default CreateInstitutionalPage;
export type { ICreateInstitutionalPageProps };
